import type { DeliveryOutcome } from '../deliveryRun/delivery';
import type { DeliveryRatingBreakdown } from '../rating/deliveryRating';
import { GIANT_ORDERS } from '../catalog/giantOrders';
import type { MissionRef } from '../catalog/mission';
import { isGiantId } from '../ids/ids';
import type { DeliveryHistoryEntry, DeliveryHistoryOutcome, DeliveryPoolEntry, PlayerEconomyState } from './types';
import { deliveryIdFromMission, missionFromDeliveryId } from './types';

export function isDeliveryAttempted(entry: DeliveryPoolEntry): boolean {
  return (
    entry.takenOnWaitingDay != null ||
    entry.status === 'failed' ||
    entry.status === 'completed' ||
    entry.bestRating > 0
  );
}

export function isDeliveryHistorySuccess(entry: DeliveryHistoryEntry): boolean {
  return entry.bestRating > 0;
}

export function upsertDeliveryHistoryEntry(
  history: DeliveryHistoryEntry[],
  mission: MissionRef,
  outcome: DeliveryOutcome | 'aborted',
  rating: DeliveryRatingBreakdown,
  poolEntry: DeliveryPoolEntry,
  sparksCollected: number,
  sparksTotal: number,
  now: number,
): DeliveryHistoryEntry[] {
  const deliveryId = deliveryIdFromMission(mission);
  const existing = history.find((e) => e.deliveryId === deliveryId);
  // Keep "completed" once bestRating > 0 so a failed replay does not demote history.
  const priorSuccess =
    poolEntry.bestRating > 0 || (existing?.bestRating ?? 0) > 0 || outcome === 'completed';
  const historyOutcome: DeliveryHistoryOutcome = priorSuccess ? 'completed' : 'failed';

  const next: DeliveryHistoryEntry = {
    deliveryId,
    giantId: mission.giantId,
    orderIndex: mission.orderIndex,
    outcome: historyOutcome,
    bestRating: Math.max(poolEntry.bestRating, existing?.bestRating ?? 0),
    lastRating: outcome === 'completed' ? rating : existing?.lastRating ?? null,
    lastSparksCollected: sparksCollected,
    lastSparksTotal: sparksTotal,
    updatedAt: now,
  };

  if (existing == null) {
    return [...history, next];
  }
  return history.map((e) => (e.deliveryId === deliveryId ? next : e));
}

/**
 * Восстанавливает giantId/orderIndex из deliveryId и отбрасывает битые записи
 * (иначе delivery_list падает в getGiantOrder на undefined[orderIndex]).
 */
export function normalizeDeliveryHistoryEntry(
  entry: DeliveryHistoryEntry,
): DeliveryHistoryEntry | null {
  const fromId = missionFromDeliveryId(entry.deliveryId);
  const giantId = isGiantId(entry.giantId)
    ? entry.giantId
    : fromId?.giantId;
  if (giantId == null) {
    return null;
  }
  const orderIndex = Number.isInteger(entry.orderIndex)
    ? entry.orderIndex
    : fromId?.orderIndex;
  if (orderIndex == null || !Number.isInteger(orderIndex) || orderIndex < 0) {
    return null;
  }
  if (GIANT_ORDERS[giantId]?.[orderIndex] == null) {
    return null;
  }
  const deliveryId =
    entry.deliveryId && entry.deliveryId.length > 0
      ? entry.deliveryId
      : deliveryIdFromMission({ giantId, orderIndex });
  return {
    ...entry,
    deliveryId,
    giantId,
    orderIndex,
  };
}

/** Миграция старых сохранений: собрать историю из пула, если поле ещё пустое; нормализовать записи. */
export function migrateDeliveryHistory(state: PlayerEconomyState): PlayerEconomyState {
  let deliveryHistory: DeliveryHistoryEntry[];
  if (Array.isArray(state.deliveryHistory) && state.deliveryHistory.length > 0) {
    deliveryHistory = state.deliveryHistory
      .map(normalizeDeliveryHistoryEntry)
      .filter((entry): entry is DeliveryHistoryEntry => entry != null);
  } else {
    deliveryHistory = state.deliveryPool
      .filter(isDeliveryAttempted)
      .map((entry): DeliveryHistoryEntry | null =>
        normalizeDeliveryHistoryEntry({
          deliveryId: entry.deliveryId,
          giantId: entry.giantId,
          orderIndex: entry.orderIndex,
          outcome: entry.bestRating > 0 ? 'completed' : 'failed',
          bestRating: entry.bestRating,
          lastRating: null,
          lastSparksCollected: 0,
          lastSparksTotal: 0,
          updatedAt: entry.updatedAt,
        }),
      )
      .filter((entry): entry is DeliveryHistoryEntry => entry != null);
  }
  return { ...state, deliveryHistory };
}

export function listDeliveryHistorySorted(state: PlayerEconomyState): DeliveryHistoryEntry[] {
  const history = state.deliveryHistory ?? [];
  return history
    .map(normalizeDeliveryHistoryEntry)
    .filter((entry): entry is DeliveryHistoryEntry => entry != null)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}
